/**
 * <TutorStudentsDeskPage> — « Mes élèves » hors live, dans le cadre du live (page).
 *
 * Wajih (04/11) : hors live, « Mes élèves » reprend le cadre du live, ses composants, moins ce qui touche à la
 * visio et à la séance. Les trois colonnes de `<TutorLivePage>`, aux mêmes largeurs :
 *  - **à gauche**, les élèves, plus calmes : leurs lignes de « Mes élèves » (`<TutorStudentRow>`), sans vidéo ni
 *    mode du son, et sous l'élève regardé sa liste d'activités du live (`<TutorLiveActivityList>`) ;
 *  - **au centre**, sa fiche (`<TutorStudentProfilePage>`), seule tant qu'aucune activité n'est ouverte : son
 *    identité une fois, sans onglet « Profil » orphelin. Une activité ouverte passe au centre du live
 *    (`<TutorLiveCenter offLive>`) : l'encart de l'activité du live, et sa fiche sous l'onglet « Profil », sans redire
 *    son visage ni son nom (la tête du centre les dit) ; ni Visio, ni « Présenter », ni « Afficher à l'élève ». La
 *    relecture des propositions du champion (`<TutorReviewStage>`), « Plus d'options »
 *    (`<TutorGenerationConfigurator>`) et l'aperçu d'une proposition survolée se posent sur le centre, comme en live ;
 *  - **à droite**, la colonne du champion (`<TutorSessionFeed>`), à largeur fixe : la mission de l'élève en tête
 *    (`<SamMissionCard viewer="tutor">`), sa session et le champ qui génère ; « Messages », le second onglet de
 *    la colonne, est la conversation avec l'élève (`<TutorConversationPage>`). Hors live, pas de bac « À décider »
 *    ni « Ailleurs » : rien n'y attend une décision.
 *
 * Personne de regardé : un seul état vide, au centre, et la colonne du champion se tait. Tant que la liste n'est pas
 * lue : sa forme, sans compte ni état vide nulle part.
 *
 * Ni barre du bas, ni zone sûre : la page vit dans la coquille de l'espace tuteur, sous son en-tête.
 *
 * **Le cadre se tient à partir de 1 024 px** (`DESK_FROM`, le seuil où le live ancre la colonne du champion), mesurés
 * sur la page ; en deçà, la liste de « Mes élèves » seule (`<TutorStudentsPage>`), l'écran poussant la fiche.
 * Les seuils et les largeurs sont ceux du live (`tutorLiveColumns`) : la pellicule et la colonne du champion.
 *
 * Contrôlée : l'élève regardé, l'activité ouverte, l'onglet de chaque colonne et la session arrivent en props.
 */
import React, { useLayoutEffect } from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UISamMission } from "../../../ports/primary/ui/types/studentHome"
import {
    type UITutorLiveActivity,
    type UITutorLiveStudent,
    type UITutorSession,
} from "../../../ports/primary/ui/types/tutorLive"
import { CHAMPIONS, type ChampionName } from "../../atoms/Avatar/ChampionAvatar"
import { Button } from "../../atoms/Buttons/Button"
import { Row } from "../../atoms/containers/Row"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"
import { CardList } from "../../molecules/CardList"
import { EmptyState } from "../../molecules/EmptyState"
import { RefreshButton } from "../../molecules/RefreshButton"
import { SamMissionCard } from "../../molecules/SamMissionCard"
import { Tabs } from "../../molecules/Tabs"
import { TutorLiveActivityList } from "../../molecules/TutorLiveActivityList"
import {
    TutorGenerationConfigurator,
    type TutorGenerationConfiguratorProps,
} from "../../organisms/TutorGenerationConfigurator"
import { TutorLiveCenter, type TutorLiveCenterProps } from "../../organisms/TutorLiveCenter"
import {
    TutorReviewPeek,
    TutorReviewStage,
    type TutorReviewPeekProps,
    type TutorReviewStageProps,
} from "../../organisms/TutorReviewStage"
import { type TutorSessionEntryProps } from "../../organisms/TutorSessionEntry"
import { TutorSessionFeed, type TutorSessionComposer } from "../../organisms/TutorSessionFeed"
import { TutorStudentRow, TutorStudentRowSkeleton } from "../../organisms/TutorStudentRow"
import { PageTemplate, PaneWidthContext } from "../../templates/PageTemplate/PageTemplate"
import {
    FEED_ANCHORED_FROM,
    FEED_WIDTH,
    FILMSTRIP_WIDTH,
    TUTOR_LIVE_WIDE_FROM,
} from "../TutorLivePage/tutorLiveColumns"
import { TutorConversationPage, type TutorConversationPageProps } from "../TutorConversationPage"
import { TutorStudentProfilePage } from "../TutorStudentProfilePage"
import { TutorStudentsPage, type TutorStudentsPageProps } from "../TutorStudentsPage"
import { GENERATE_FOR_STUDENTS_HINT } from "../TutorStudentsPage/TutorStudentsPage"

/** À partir de cette largeur de page, le cadre du live (le seuil où le live ancre la colonne du champion). */
export const DESK_FROM = FEED_ANCHORED_FROM

/** Les élèves : leurs lignes, l'élève regardé, et ce que la liste de « Mes élèves » offre aujourd'hui. */
export type TutorStudentsDeskStudentsZone = TutorStudentsPageProps & {
    /** L'élève regardé : sa ligne est marquée, sa liste d'activités dessous. */
    selectedId?: string | number | null
}

/** La liste d'activités de l'élève regardé, celle du live. */
export type TutorStudentsDeskActivitiesZone = {
    /** L'élève : son visage sur la ligne qu'il a devant lui. */
    student: { firstname: string; avatarUrl?: string }
    list: UITutorLiveActivity[]
    /** D'autres faites attendent derrière « Voir plus ». */
    moreDone?: boolean
    /** L'activité ouverte au centre : sa ligne est teintée. */
    openActivityId?: string
    onOpenActivity: (activityId: string) => void
    onReorder?: (activityIds: string[]) => void
    onSeeMoreDone?: () => void
}

/** Le centre : rien de choisi, l'élève choisi qui se lit (la forme de sa fiche), ou le centre du live hors live. */
export type TutorStudentsDeskCenterZone =
    | { kind: "empty" }
    | { kind: "loading" }
    | {
          kind: "student"
          /** L'élève, tel que le centre du live le lit (sa tête, son objectif). */
          student: UITutorLiveStudent
          /** Sans la bascule du mode du son (`gestures.onChangeMode`) : hors live, le centre ne l'offre pas. */
          center: Omit<
              TutorLiveCenterProps,
              "students" | "selectedId" | "renderVideo" | "phone" | "offLive" | "profileTab" | "present"
          >
      }

export type UITutorStudentsDeskSamTab = "sam" | "messages"

/** La colonne du champion, hors live : sa session, la mission, la génération, et la conversation en second onglet. */
export type TutorStudentsDeskSamZone = {
    tab: UITutorStudentsDeskSamTab
    onChangeTab: (tab: UITutorStudentsDeskSamTab) => void
    /** La session de l'élève regardé avec le champion (la génération, ses lignes). Absente : aucun élève regardé. */
    session?: UITutorSession | null
    /** La mission du champion de l'élève, celle que son accueil lui montre, et son prénom. Absente : pas de mission. */
    mission?: (UISamMission & { firstname: string }) | null
    composer?: TutorSessionComposer
    onResult?: TutorSessionEntryProps["onResult"]
    onGeneration?: TutorSessionEntryProps["onGeneration"]
    onPeek?: TutorSessionEntryProps["onPeek"]
    /** La relecture des propositions, au centre. */
    review?: TutorReviewStageProps | null
    /** L'aperçu d'une proposition survolée, au centre. */
    peek?: TutorReviewPeekProps | null
    /** « Plus d'options » du champ : le configurateur de la génération, au centre. */
    configurator?: TutorGenerationConfiguratorProps | null
    /** La conversation avec l'élève. Absente : pas d'onglet « Messages ». */
    messages?: TutorConversationPageProps | null
    /** Des messages de l'élève attendent : le compte sur l'onglet. */
    unread?: number
}

export type TutorStudentsDeskPageProps = {
    students: TutorStudentsDeskStudentsZone
    /** La liste d'activités de l'élève regardé. Absente : rien sous sa ligne (elle charge). */
    activities?: TutorStudentsDeskActivitiesZone | null
    center: TutorStudentsDeskCenterZone
    sam: TutorStudentsDeskSamZone
    /** Le champion de l'espace : il signe la colonne. */
    helper: ChampionName
    /**
     * Le cadre du live est-il là (≥ `DESK_FROM`) : la page le mesure sur son conteneur, une seule fois, et le dit à
     * l'écran, qui ne lit l'élève regardé qu'à cette condition. Rappelé à chaque passage du seuil.
     */
    onDeskChange?: (desk: boolean) => void
    testID?: string
}

const TEXTS = {
    /** Sans compte tant que la liste n'est pas lue : jamais « 0 » le temps d'une lecture. */
    title: (count: number | null) => (count == null ? "Élèves" : `Élèves · ${count}`),
    generate: "Activité commune",
    generateHint: GENERATE_FOR_STUDENTS_HINT,
    empty: "Aucun élève",
    emptyTitle: "Aucun élève ouvert",
    emptyMessage: "Choisis un élève : sa fiche ici, sa session et ses messages à droite.",
    samTabs: "Ce que montre la colonne",
    messages: "Messages",
}

const noop = () => {}
const NO_ELSEWHERE: never[] = []

export function TutorStudentsDeskPage(props: TutorStudentsDeskPageProps) {
    const { students, activities, center, sam, helper, onDeskChange, testID } = props
    const desk = useWidthAtLeast(DESK_FROM)
    // Avant la peinture : l'écran et la page ne voient jamais le seuil différemment, pas même une image.
    useLayoutEffect(() => onDeskChange?.(desk.atLeast), [desk.atLeast, onDeskChange])
    const wideWidth = useWidthAtLeast(TUTOR_LIVE_WIDE_FROM)
    const onLayout: typeof desk.onLayout = (event) => {
        desk.onLayout(event)
        wideWidth.onLayout(event)
    }
    const size = wideWidth.atLeast ? "wide" : "narrow"

    if (!desk.atLeast) {
        return (
            <View style={styles.flex} onLayout={onLayout} testID={testID}>
                <TutorStudentsPage {...students} />
            </View>
        )
    }

    // Comme en live : la relecture, sinon « Plus d'options », sinon l'aperçu d'une proposition survolée.
    const centerOverlay =
        sam.review?.open === true ? (
            <TutorReviewStage {...sam.review} />
        ) : sam.configurator?.open === true ? (
            <TutorGenerationConfigurator {...sam.configurator} />
        ) : sam.peek != null ? (
            <TutorReviewPeek {...sam.peek} />
        ) : null

    let centerBody: React.ReactNode = null
    if (center.kind === "student" && center.center.focus != null) {
        centerBody = (
            <View style={styles.centerPane}>
                <TutorLiveCenter
                    {...center.center}
                    students={[center.student]}
                    selectedId={center.student.id}
                    profileTab
                    offLive
                />
            </View>
        )
    } else if (center.kind === "student" && center.center.profile != null) {
        // Aucune activité ouverte : sa fiche seule. La tête du centre et son onglet « Profil » la redoubleraient.
        centerBody = <TutorStudentProfilePage {...center.center.profile} />
    } else if (center.kind !== "empty") {
        centerBody = <TutorStudentProfilePage profile={null} loading tab="overview" onChangeTab={noop} />
    } else if (students.loading !== true && students.students.length > 0) {
        // La liste lue, personne de regardé : le seul état vide de la page (ni avant la lecture, ni sans élève).
        centerBody = (
            <View style={styles.empty}>
                <EmptyState iconName="user-graduate" title={TEXTS.emptyTitle} message={TEXTS.emptyMessage} />
            </View>
        )
    }

    const samTabs = [
        // Le premier onglet porte le prénom du champion de l'espace, jamais un nom écrit ici.
        { key: "sam" as const, label: CHAMPIONS[helper].label },
        ...(sam.messages != null ? [{ key: "messages" as const, label: TEXTS.messages, count: sam.unread }] : []),
    ]
    const samTab = sam.messages != null ? sam.tab : "sam"

    return (
        <View style={styles.root} onLayout={onLayout} testID={testID}>
            <DeskStudentsColumn students={students} activities={activities} size={size} />

            <View style={styles.center}>
                {centerBody}
                {centerOverlay}
            </View>

            <View style={styles.sam(FEED_WIDTH[size])}>
                {samTabs.length > 1 && (
                    <Tabs
                        items={samTabs}
                        value={samTab}
                        onChange={sam.onChangeTab}
                        size="sm"
                        accessibilityLabel={TEXTS.samTabs}
                    />
                )}
                <View style={styles.flex}>
                    {samTab === "messages" && sam.messages != null ? (
                        <TutorConversationPage {...sam.messages} />
                    ) : sam.session == null ? null : ( // Personne de regardé : la colonne se tait, le centre le dit.
                        <TutorSessionFeed
                            helper={helper}
                            session={sam.session}
                            elsewhere={NO_ELSEWHERE}
                            // Hors live, rien n'attend de décision : ni bac, ni « Ailleurs », ni autre élève à regarder.
                            onSelectStudent={noop}
                            onAction={noop}
                            onDismiss={noop}
                            onResult={sam.onResult}
                            onGeneration={sam.onGeneration}
                            onPeek={sam.onPeek}
                            peekId={sam.peek != null ? String(sam.peek.proposal.id) : null}
                            composer={sam.composer}
                            filter={{ id: sam.session.student.id, firstname: sam.session.student.firstname }}
                            ribbon={
                                // Comme le ruban du live, la mission s'efface le temps qu'un réglage est déplié dans le champ.
                                sam.mission != null && sam.composer?.editor == null ? (
                                    <SamMissionCard
                                        done={sam.mission.done}
                                        total={sam.mission.total}
                                        viewer="tutor"
                                        student={{ firstname: sam.mission.firstname }}
                                    />
                                ) : null
                            }
                        />
                    )}
                </View>
            </View>
        </View>
    )
}

/**
 * La colonne des élèves, à part : la page se re-rend à chaque frappe dans le champ du champion, à chaque pas d'une
 * génération ; la liste, elle, ne change qu'avec ses élèves, l'élève regardé et ses activités.
 */
const DeskStudentsColumn = React.memo(function DeskStudentsColumn(props: {
    students: TutorStudentsDeskStudentsZone
    activities: TutorStudentsDeskActivitiesZone | null | undefined
    size: "wide" | "narrow"
}) {
    const { students, activities, size } = props
    const selectedId = students.selectedId ?? null
    return (
        <View style={styles.list(FILMSTRIP_WIDTH[size])}>
            <View style={styles.head}>
                <SectionLabel>{TEXTS.title(students.loading === true ? null : students.students.length)}</SectionLabel>
                {students.onRefresh != null && (
                    <RefreshButton refreshing={students.refreshing === true} onRefresh={students.onRefresh} />
                )}
            </View>
            {/* En tête, sous le titre (la colonne est trop étroite pour les deux) : visible quelle que soit la liste. */}
            {students.students.length > 0 && students.onGenerateForStudents != null && (
                <Row>
                    <Button
                        label={TEXTS.generate}
                        iconName="plus"
                        variant="outline"
                        size="sm"
                        onPress={students.onGenerateForStudents}
                        accessibilityHint={TEXTS.generateHint}
                    />
                </Row>
            )}
            {/* La liste de « Mes élèves », ses états compris (son squelette, sa panne et « Réessayer », le tirer). */}
            <PaneWidthContext.Provider value={FILMSTRIP_WIDTH[size]}>
                <PageTemplate
                    padding="0"
                    loading={students.loading === true}
                    skeleton={<TutorStudentsDeskListSkeleton />}
                    error={students.error}
                    onRetry={students.onRetry}
                    refreshing={students.refreshing}
                    onRefresh={students.onRefresh}
                >
                    {students.students.length === 0 ? (
                        <EmptyState iconName="user-graduate" title={TEXTS.empty} />
                    ) : (
                        <CardList>
                            {students.students.flatMap((student) => {
                                const isSelected = student.id === selectedId
                                const row = (
                                    <TutorStudentRow
                                        key={student.id}
                                        student={student}
                                        selected={isSelected}
                                        compact
                                        onOpenProfile={
                                            students.onOpenProfile != null
                                                ? () => students.onOpenProfile?.(student)
                                                : undefined
                                        }
                                        onOpenMessages={
                                            students.onOpenMessages != null
                                                ? () => students.onOpenMessages?.(student)
                                                : undefined
                                        }
                                        testID={`tutor-student-${student.id}`}
                                    />
                                )
                                if (!isSelected || activities == null) return [row]
                                return [
                                    row,
                                    <View key={`${student.id}-activities`} style={styles.activities}>
                                        <TutorLiveActivityList
                                            activities={activities.list}
                                            student={activities.student}
                                            openActivityId={activities.openActivityId}
                                            onOpenActivity={activities.onOpenActivity}
                                            onReorder={activities.onReorder}
                                            onSeeMoreDone={activities.onSeeMoreDone}
                                            moreDone={activities.moreDone}
                                            defaultDoneOpen
                                        />
                                    </View>,
                                ]
                            })}
                        </CardList>
                    )}
                </PageTemplate>
            </PaneWidthContext.Provider>
        </View>
    )
})

const styles = StyleSheet.create((theme) => ({
    root: { flex: 1, minHeight: 0, flexDirection: "row", backgroundColor: theme.colors.surface },
    flex: { flex: 1, minHeight: 0 },
    list: (width: number) => ({
        width,
        minHeight: 0,
        gap: theme.spacing["2"],
        paddingVertical: theme.spacing["3"],
        paddingLeft: theme.spacing["4"],
        paddingRight: theme.spacing["3"],
        borderRightWidth: 1,
        borderRightColor: theme.colors["border-subtle"],
    }),
    head: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing["2"],
        minHeight: 32,
    },
    activities: { paddingHorizontal: theme.spacing["2"], paddingBottom: theme.spacing["2"] },
    center: {
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        position: "relative" as const,
    },
    centerPane: {
        flex: 1,
        minHeight: 0,
        paddingHorizontal: theme.spacing["4"],
        paddingVertical: theme.spacing["3"],
    },
    empty: { flex: 1, alignItems: "center" as const, justifyContent: "center" as const, padding: theme.spacing["4"] },
    sam: (width: number) => ({
        width,
        minHeight: 0,
        gap: theme.spacing["2"],
        padding: theme.spacing["3"],
        borderLeftWidth: 1,
        borderLeftColor: theme.colors["border-subtle"],
    }),
}))

/** La liste au premier chargement : trois lignes resserrées dans la carte-liste. */
function TutorStudentsDeskListSkeleton() {
    return (
        <CardList>
            <TutorStudentRowSkeleton compact />
            <TutorStudentRowSkeleton compact />
            <TutorStudentRowSkeleton compact />
        </CardList>
    )
}
